import { useCallback, useState } from "react";
import { createPaymentConfiguration } from "../services/payments";
import { useUserState } from "../state/user";
import useProfessionalAccountStatus from "./useProfessionalAccountStatus";
import { CreatePaymentConfigurationInput } from "../types/payments";

/** The form only supplies the billing entity details; the ids come from the profile. */
export type PaymentConfigurationFormValues = Omit<
  CreatePaymentConfigurationInput,
  "paymentId" | "skuId"
>;

export default function useCreatePaymentConfiguration() {
  const { token } = useUserState();
  const { paymentId, skuId } = useProfessionalAccountStatus();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(
    async (values: PaymentConfigurationFormValues): Promise<string> => {
      if (!token || !paymentId || !skuId) {
        throw new Error("Missing professional account details");
      }
      try {
        setLoading(true);
        setError(null);
        const { id } = await createPaymentConfiguration({
          authToken: token,
          input: { ...values, paymentId, skuId }
        });
        return id;
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [token, paymentId, skuId]
  );

  return { create, loading, error };
}
